import * as Sentry from '@sentry/react';
import axios from 'axios';
import { getAccessToken, getRefreshToken, saveTokens, clearTokens } from './auth.js';

const SKIP_AUTH_URLS = ['/auth/google', '/auth/refresh', '/auth/google/callback'];

const apiClient = axios.create({
  baseURL: (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, ''),
  timeout: 120000,
});

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const shouldSkip = SKIP_AUTH_URLS.some((url) => config.url.includes(url));
  if (!shouldSkip) {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Response interceptor — handle 401 with one refresh attempt.
// If a refresh is already in progress when a second 401 arrives, the second
// request is queued and retried once the refresh resolves rather than
// immediately logging the user out.
let isRefreshing = false;
let pendingRequests = [];

function resolvePending(token) {
  pendingRequests.forEach(({ resolve }) => resolve(token));
  pendingRequests = [];
}

function rejectPending(err) {
  pendingRequests.forEach(({ reject }) => reject(err));
  pendingRequests = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthUrl = SKIP_AUTH_URLS.some((url) => originalRequest.url.includes(url));

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthUrl) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Queue this request; retry it once the in-flight refresh completes.
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      isRefreshing = true;
      try {
        const refreshToken = getRefreshToken();
        const { data } = await apiClient.post('/auth/refresh', {
          refresh_token: refreshToken,
        });
        saveTokens(data.access_token, data.refresh_token);
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        resolvePending(data.access_token);
        isRefreshing = false;
        return apiClient(originalRequest);
      } catch (refreshError) {
        rejectPending(refreshError);
        isRefreshing = false;
        clearTokens();
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }

    Sentry.captureException(error, {
      extra: {
        url: originalRequest?.url,
        method: originalRequest?.method,
        status: error.response?.status,
        responseData: error.response?.data,
      },
    });
    return Promise.reject(error);
  }
);

// Named endpoint functions
export function loginWithCode(code) {
  return apiClient.post(`/auth/google/callback?code=${encodeURIComponent(code)}`);
}

export function refreshTokens() {
  return apiClient.post('/auth/refresh', { refresh_token: getRefreshToken() });
}

export function logout(refreshToken) {
  return apiClient.post('/auth/logout', { refresh_token: refreshToken });
}

export function getMe() {
  return apiClient.get('/auth/me');
}

export async function streamQuery(question, threadId, callbacks) {
  const { onSources, onAnswer, onError } = callbacks;
  const baseURL = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
  const token = getAccessToken();

  let response;
  try {
    response = await fetch(`${baseURL}/query/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ question, thread_id: threadId }),
    });
  } catch (err) {
    Sentry.captureException(err, { extra: { question, threadId } });
    onError(null, null);
    return;
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    onError(err.detail ?? null, response.status);
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split('\n\n');
    buffer = blocks.pop();

    for (const block of blocks) {
      let eventType = null;
      let dataStr = null;
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) eventType = line.slice(6).trim();
        if (line.startsWith('data:'))  dataStr  = line.slice(5).trim();
      }
      if (!eventType || !dataStr) continue;
      const data = JSON.parse(dataStr);
      if (eventType === 'sources')     onSources(data.books);
      else if (eventType === 'answer') onAnswer(data);
      else if (eventType === 'error') {
        Sentry.captureMessage('SSE stream error', {
          level: 'error',
          extra: { detail: data.detail, question, threadId },
        });
        onError(data.detail, null);
      }
    }
  }
}

export function createCheckoutSession() {
  return apiClient.post('/stripe/checkout-session');
}

export function getSubscription() {
  return apiClient.get('/stripe/subscription');
}

export function getPrice() {
  return apiClient.get('/stripe/price');
}

export function cancelSubscription() {
  return apiClient.post('/stripe/subscription/cancel');
}

export function getMyQuota() {
  return apiClient.get('/quota/me');
}

export function getConversationHistory(threadId) {
  return apiClient.get(`/chat/history?thread_id=${threadId}`);
}

export function deleteHistory(threadId) {
  return apiClient.delete(`/chat/history?thread_id=${encodeURIComponent(threadId)}`);
}

export function getResellerDashboard() {
  return apiClient.get('/reseller/dashboard');
}

export function generateInviteLink() {
  return apiClient.post('/reseller/invite');
}

export function addSeats(quantity) {
  return apiClient.post('/reseller/seats/add', { quantity });
}

export function suspendResellerUser(userId) {
  return apiClient.post(`/reseller/users/${userId}/suspend`);
}

export function restoreResellerUser(userId) {
  return apiClient.post(`/reseller/users/${userId}/restore`);
}

export function removeResellerUser(userId) {
  return apiClient.delete(`/reseller/users/${userId}`);
}

export function applyReseller() {
  return apiClient.post('/reseller/apply');
}

export default apiClient;
